import {
	Component,
	OnDestroy,
	OnInit,
	ViewChild,
	ElementRef,
	Input,
	Output,
	EventEmitter
} from '@angular/core';
import { CustomerService } from '../../services/customer.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UntypedFormControl, FormGroup, FormControl } from '@angular/forms';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { PrintService } from '../../services/print.service';

@Component({
	selector: 'app-new-customers',
	templateUrl: './new-customers.component.html',
	styleUrls: ['./new-customers.component.scss'],
})
export class NewCustomersComponent implements OnInit, OnDestroy {
	@Input() flip = 'inactive';
	@Output() viewData = new EventEmitter < any > ();
	@ViewChild('print_table') printtable: ElementRef;

	fromdate: Date;
	todate: Date;
	searchTerm = '';
	sortColumn = '';
	sortDirection: 'asc' | 'desc' = 'asc';
	filteredData: any[] = [];
	data: any = false;
	total = 0;
	title = 'New Customers';
	sending = false;
	viewing = false;
	datasub: any = false;

	hideshowunits = new UntypedFormControl(true);
	hideshowsales = new UntypedFormControl(true);
	hideshowcost = new UntypedFormControl(false);
	defaultlocation = new UntypedFormControl('');
	user: any = false;
	config: any = false;
	ref_location: any = false;
	filename = '';
	isMoov = false;
	color = 'blue';

	range: FormGroup;

	private static readonly THEME_PALETTE: Record<string, { light: [number, number, number]; base: [number, number, number]; dark: [number, number, number] }> = {
		purple:  { light: [206, 147, 216], base: [156,  39, 176], dark: [ 74,  20, 140] },
		primary: { light: [206, 147, 216], base: [156,  39, 176], dark: [ 74,  20, 140] },
		blue:    { light: [144, 202, 249], base: [ 33, 150, 243], dark: [ 25, 118, 210] },
		info:    { light: [128, 222, 234], base: [  0, 188, 212], dark: [  0,  96, 100] },
		green:   { light: [165, 214, 167], base: [ 76, 175,  80], dark: [ 27,  94,  32] },
		success: { light: [165, 214, 167], base: [ 76, 175,  80], dark: [ 27,  94,  32] },
		orange:  { light: [255, 204, 128], base: [255, 152,   0], dark: [230,  81,   0] },
		warning: { light: [255, 224, 130], base: [255, 193,   7], dark: [255, 143,   0] },
		red:     { light: [239, 154, 154], base: [244,  67,  54], dark: [183,  28,  28] },
		danger:  { light: [239, 154, 154], base: [244,  67,  54], dark: [183,  28,  28] },
		rose:    { light: [244, 143, 177], base: [233,  30,  99], dark: [136,  14,  79] },
	};

	get palette() {
		return NewCustomersComponent.THEME_PALETTE[this.color] || NewCustomersComponent.THEME_PALETTE['blue'];
	}

	get themeVars(): { [key: string]: string } {
		const p = this.palette;
		const rgb  = (c: number[]) => `rgb(${c[0]},${c[1]},${c[2]})`;
		const rgba = (c: number[], a: number) => `rgba(${c[0]},${c[1]},${c[2]},${a})`;
		return {
			'--nc-accent':        rgb(p.base),
			'--nc-accent-dark':   rgb(p.dark),
			'--nc-accent-light':  rgb(p.light),
			'--nc-accent-tint':   rgba(p.base, 0.07),
			'--nc-accent-tint-2': rgba(p.base, 0.14),
			'--nc-accent-border': rgba(p.base, 0.20),
			'--nc-accent-bg':     rgba(p.light, 0.16),
		};
	}

	get topAccount(): string {
		if (!this.data || !this.data.length) {
			return '—';
		}
		const top = this.data.reduce((best: any, row: any) => ((+row.totalspend || 0) > (+best.totalspend || 0) ? row : best), this.data[0]);
		return top?.name || '—';
	}

	constructor(
		private printService: PrintService,
		public globalSearchService: GlobalSearchService,
		private customerService: CustomerService,
		private modalService: NgbModal
	) {
		this.color = 'info';
	}

	ngOnInit(): void {
		const now = new Date();
		const firstDayPrevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
	
		this.range = new FormGroup({
			start: new FormControl(firstDayPrevMonth),
			end: new FormControl(now),
		});
	
		const updateTitles = () => {
			const startStr = this.range.controls.start.value?.toLocaleDateString() || '';
			const endStr = this.range.controls.end.value?.toLocaleDateString() || '';
			this.filename = `New Customers ${startStr} - ${endStr}`;
			this.title = `New Customers ${startStr} - ${endStr}`;
		};
	
		updateTitles();
		this.loadData();
	
		this.range.valueChanges.subscribe(() => {
			updateTitles();
		});
	
		this.globalSearchService.configsubscription.subscribe((cfg: any) => {
			this.config = cfg;
			this.isMoov = cfg?.env?.client === 'moov' || cfg?.env?.client === 'moov_sandbox';
		});

		this.globalSearchService.user.subscribe(results => {
			if (this.user && results.user.defaultlocation.loccode !== this.ref_location) {
				this.defaultlocation.setValue(this.user.user.defaultlocation.loccode);
				this.ref_location = false;
			}

			this.user = results;
			if (!this.ref_location) {
				this.ref_location = this.user.user.defaultlocation.loccode;
			}
		});
	}


	onSearchChange(): void {
		const term = this.searchTerm.toLowerCase();
		if (!term) {
			this.filteredData = this.data;
			return;
		}

		this.filteredData = this.data.filter((item: any) =>
			(item.debtorno?.toString().toLowerCase().includes(term)) ||
			(item.name?.toLowerCase().includes(term)) ||
			(item.address1?.toLowerCase().includes(term)) ||
			(item.address3?.toLowerCase().includes(term)) ||
			(item.address4?.toLowerCase().includes(term)) ||
			(item.address5?.toLowerCase().includes(term)) ||
			(item.salesmanname?.name?.toLowerCase().includes(term)) ||
			(item.details?.cosm_license?.toLowerCase().includes(term))
		);
	}

	onDateRangeChange(_event: any): void {
		if (this.range.controls.start.value && this.range.controls.end.value) {
			this.loadData();
		}
	}

	loadData() {
		
		const data = {
			fromdate: this.range.get('start').value,
			todate: this.range.get('end').value
		};

		this.sending = true;

		this.datasub = this.customerService.getNewCustomers(data).subscribe((r: any) => {
			this.total = r.length;
			this.data = r;
			this.filteredData = [...this.data];
			this.onSearchChange();
			this.sending = false;
		});
	}

	toggleFlip(data: any, title: string) {
		const prev = this.flip;
		this.flip = data ? (this.flip === 'active' ? 'inactive' : 'active') : 'inactive';
	
		if (this.flip === 'active') {
			if (prev !== 'active') {
				this.viewing = data;
				this.title = title;
				setTimeout(() => {
					this.viewData.emit({ data, title: this.title , comp: 'newcustomer' });
				}, 0);
			}
		} else {
			setTimeout(() => {
				this.viewData.emit(false);
			}, 0);
		}
	}

	exportPdf() {
		this.sending = true;
		const encoded: string = this.globalSearchService.base64encode(this.printtable.nativeElement.innerHTML);
		const timestamp = new Date().toLocaleString();
	
		const data = {
			content: encoded,
			filename: this.filename,
			title: this.title,
			subtitle: `Created: ${timestamp}`,
			landscape: true
		};
	
		this.printService.pdf(data).subscribe((result: any) => {
			this.sending = false;
			this.globalSearchService.downloadPdf(result.content, data.filename);
		});
	}
	
	exportXls() {
		this.sending = true;
		const encoded: string = this.globalSearchService.base64encode(this.printtable.nativeElement.innerHTML);
		const timestamp = new Date().toLocaleString();
	
		const data = {
			content: encoded,
			filename: this.filename,
			title: this.title,
			subtitle: `Created: ${timestamp}`
		};
	
		this.printService.xls(data).subscribe((result: any) => {
			this.sending = false;
			this.globalSearchService.downloadXls(result.content, data.filename);
		});
	}


	sort(column: string): void {
		if (this.sortColumn === column) {
			this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
		} else {
			this.sortColumn = column;
			this.sortDirection = 'asc';
		}

		this.filteredData.sort((a: any, b: any) => {
			const resolve = (row: any) => column === 'cosm_license' ? (row.details?.cosm_license ?? '') : (row[column] ?? '');
			const aValue = resolve(a).toString().toLowerCase();
			const bValue = resolve(b).toString().toLowerCase();

			if (!isNaN(+aValue) && !isNaN(+bValue)) {
				return this.sortDirection === 'asc' ? +aValue - +bValue : +bValue - +aValue;
			}

			return this.sortDirection === 'asc' ?
				aValue.localeCompare(bValue) :
				bValue.localeCompare(aValue);
		});
	}

	ngOnDestroy() {
		if (this.datasub) {
			this.datasub.unsubscribe();
		}
	}
}